import React, { useState, useEffect, useCallback } from 'react';
import {
  Target,
  Plus,
  Pencil,
  Trash2,
  CheckCircle,
  XCircle,
  ShieldCheck,
  ChevronDown,
  Layers,
  FileText,
  Building2,
  Server,
  MapPin,
  Workflow,
  Briefcase,
  Package,
  Link2,
  Loader2,
  AlertTriangle,
} from 'lucide-react';
import ConfirmationModal from '@/components/ConfirmationModal';
import PageInfoButton from '@/components/PageInfoButton';

/* ------------------------------------------------------------------ */
/*  Types                                                              */
/* ------------------------------------------------------------------ */

type ScopeType =
  | 'department'
  | 'system'
  | 'location'
  | 'process'
  | 'business_unit'
  | 'product_line'
  | 'supply_chain';

type StatementType =
  | 'inclusion'
  | 'exclusion'
  | 'limitation'
  | 'assumption'
  | 'interface';

interface ScopeItem {
  id: number;
  assignment_id: number;
  scope_type: ScopeType;
  scope_name: string;
  scope_description: string;
  is_included: boolean;
  justification: string;
  created_at?: string;
  updated_at?: string;
}

interface BoundaryStatement {
  id: number;
  assignment_id: number;
  statement_type: StatementType;
  statement_text: string;
  is_approved: boolean;
  approved_by?: string;
  approved_at?: string;
  created_at?: string;
  updated_at?: string;
}

interface ScopeSummary {
  total_items: number;
  included: number;
  excluded: number;
  approved_statements: number;
  total_statements: number;
}

interface FrameworkAssignment {
  id: number;
  framework_name?: string;
  version_label?: string;
  [key: string]: any;
}

/* ------------------------------------------------------------------ */
/*  Auth helpers                                                       */
/* ------------------------------------------------------------------ */

const getCsrfToken = () =>
  document.cookie.match(/(?:^|;\s*)csrf_token=([^;]+)/)?.[1] ?? '';

const authHeaders = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
  'Content-Type': 'application/json',
  'X-CSRF-Token': getCsrfToken(),
});

/* ------------------------------------------------------------------ */
/*  API helpers                                                        */
/* ------------------------------------------------------------------ */

const API_BASE = '/api/compliance/scope';

async function apiFetch<T>(url: string, opts?: RequestInit): Promise<T> {
  const res = await fetch(url, {
    headers: authHeaders(),
    ...opts,
  });
  if (!res.ok) {
    const body = await res.json().catch(() => ({}));
    throw new Error(body.message || body.error || `Request failed (${res.status})`);
  }
  return res.json();
}

/* ------------------------------------------------------------------ */
/*  Style maps                                                         */
/* ------------------------------------------------------------------ */

const SCOPE_TYPE_CONFIG: Record<ScopeType, { label: string; color: string; bg: string; icon: React.ElementType }> = {
  department:    { label: 'Department',    color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200',    icon: Building2 },
  system:        { label: 'System',        color: 'text-indigo-700', bg: 'bg-indigo-50 border-indigo-200', icon: Server },
  location:      { label: 'Location',      color: 'text-green-700',  bg: 'bg-green-50 border-green-200',   icon: MapPin },
  process:       { label: 'Process',       color: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200',   icon: Workflow },
  business_unit: { label: 'Business Unit', color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200', icon: Briefcase },
  product_line:  { label: 'Product Line',  color: 'text-pink-700',   bg: 'bg-pink-50 border-pink-200',     icon: Package },
  supply_chain:  { label: 'Supply Chain',  color: 'text-teal-700',   bg: 'bg-teal-50 border-teal-200',     icon: Link2 },
};

const STATEMENT_TYPE_CONFIG: Record<StatementType, { label: string; color: string; bg: string }> = {
  inclusion:  { label: 'Inclusion',  color: 'text-green-700',  bg: 'bg-green-50 border-green-200' },
  exclusion:  { label: 'Exclusion',  color: 'text-red-700',    bg: 'bg-red-50 border-red-200' },
  limitation: { label: 'Limitation', color: 'text-amber-700',  bg: 'bg-amber-50 border-amber-200' },
  assumption: { label: 'Assumption', color: 'text-blue-700',   bg: 'bg-blue-50 border-blue-200' },
  interface:  { label: 'Interface',  color: 'text-purple-700', bg: 'bg-purple-50 border-purple-200' },
};

const SCOPE_TYPES: ScopeType[] = ['department', 'system', 'location', 'process', 'business_unit', 'product_line', 'supply_chain'];
const STATEMENT_TYPES: StatementType[] = ['inclusion', 'exclusion', 'limitation', 'assumption', 'interface'];

/* ------------------------------------------------------------------ */
/*  Component                                                          */
/* ------------------------------------------------------------------ */

const ScopeDefinition: React.FC = () => {
  /* --- framework selector state --- */
  const [assignments, setAssignments] = useState<FrameworkAssignment[]>([]);
  const [selectedAssignmentId, setSelectedAssignmentId] = useState<number | null>(null);
  const [loadingAssignments, setLoadingAssignments] = useState(true);

  /* --- scope data --- */
  const [scopeItems, setScopeItems] = useState<ScopeItem[]>([]);
  const [boundaries, setBoundaries] = useState<BoundaryStatement[]>([]);
  const [summary, setSummary] = useState<ScopeSummary | null>(null);
  const [loadingData, setLoadingData] = useState(false);

  /* --- modals --- */
  const [scopeModalOpen, setScopeModalOpen] = useState(false);
  const [boundaryModalOpen, setBoundaryModalOpen] = useState(false);
  const [editingScopeItem, setEditingScopeItem] = useState<ScopeItem | null>(null);
  const [editingBoundary, setEditingBoundary] = useState<BoundaryStatement | null>(null);

  /* --- delete confirm --- */
  const [deleteConfirm, setDeleteConfirm] = useState<{ type: 'scope' | 'boundary'; id: number } | null>(null);

  /* --- form state: scope item --- */
  const [scopeForm, setScopeForm] = useState({
    scope_type: 'department' as ScopeType,
    scope_name: '',
    scope_description: '',
    is_included: true,
    justification: '',
  });

  /* --- form state: boundary --- */
  const [boundaryForm, setBoundaryForm] = useState({
    statement_type: 'inclusion' as StatementType,
    statement_text: '',
  });

  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  /* ---------------------------------------------------------------- */
  /*  Load framework assignments                                       */
  /* ---------------------------------------------------------------- */

  useEffect(() => {
    (async () => {
      try {
        const data = await apiFetch<any>('/api/compliance/execution/company/frameworks');
        const list: FrameworkAssignment[] = Array.isArray(data) ? data : data.data ?? data.assignments ?? [];
        setAssignments(list);
      } catch {
        setAssignments([]);
      } finally {
        setLoadingAssignments(false);
      }
    })();
  }, []);

  /* ---------------------------------------------------------------- */
  /*  Load scope data when assignment selected                         */
  /* ---------------------------------------------------------------- */

  const loadScopeData = useCallback(async (assignmentId: number) => {
    setLoadingData(true);
    setError('');
    try {
      const [itemsRes, summaryRes] = await Promise.all([
        apiFetch<any>(`${API_BASE}/${assignmentId}`),
        apiFetch<any>(`${API_BASE}/${assignmentId}/summary`),
      ]);

      const items: ScopeItem[] = Array.isArray(itemsRes) ? itemsRes : itemsRes.items ?? itemsRes.data ?? [];
      const bounds: BoundaryStatement[] = Array.isArray(itemsRes)
        ? []
        : itemsRes.boundaries ?? itemsRes.boundary_statements ?? [];

      setScopeItems(items);
      setBoundaries(bounds);
      setSummary(summaryRes);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setLoadingData(false);
    }
  }, []);

  useEffect(() => {
    if (selectedAssignmentId) {
      loadScopeData(selectedAssignmentId);
    } else {
      setScopeItems([]);
      setBoundaries([]);
      setSummary(null);
    }
  }, [selectedAssignmentId, loadScopeData]);

  /* ---------------------------------------------------------------- */
  /*  Scope item CRUD                                                  */
  /* ---------------------------------------------------------------- */

  const openScopeModal = (item?: ScopeItem) => {
    if (item) {
      setEditingScopeItem(item);
      setScopeForm({
        scope_type: item.scope_type,
        scope_name: item.scope_name,
        scope_description: item.scope_description,
        is_included: item.is_included,
        justification: item.justification,
      });
    } else {
      setEditingScopeItem(null);
      setScopeForm({ scope_type: 'department', scope_name: '', scope_description: '', is_included: true, justification: '' });
    }
    setScopeModalOpen(true);
  };

  const saveScopeItem = async () => {
    if (!selectedAssignmentId) return;
    if (!scopeForm.scope_name.trim()) { setError('Scope name is required.'); return; }

    setSaving(true);
    setError('');
    try {
      if (editingScopeItem) {
        await apiFetch(`${API_BASE}/items/${editingScopeItem.id}`, {
          method: 'PUT',
          body: JSON.stringify(scopeForm),
        });
      } else {
        await apiFetch(`${API_BASE}/${selectedAssignmentId}/items`, {
          method: 'POST',
          body: JSON.stringify(scopeForm),
        });
      }
      setScopeModalOpen(false);
      await loadScopeData(selectedAssignmentId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteScopeItem = async (id: number) => {
    if (!selectedAssignmentId) return;
    try {
      await apiFetch(`${API_BASE}/items/${id}`, { method: 'DELETE' });
      await loadScopeData(selectedAssignmentId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Boundary statement CRUD                                          */
  /* ---------------------------------------------------------------- */

  const openBoundaryModal = (b?: BoundaryStatement) => {
    if (b) {
      setEditingBoundary(b);
      setBoundaryForm({ statement_type: b.statement_type, statement_text: b.statement_text });
    } else {
      setEditingBoundary(null);
      setBoundaryForm({ statement_type: 'inclusion', statement_text: '' });
    }
    setBoundaryModalOpen(true);
  };

  const saveBoundary = async () => {
    if (!selectedAssignmentId) return;
    if (!boundaryForm.statement_text.trim()) { setError('Statement text is required.'); return; }

    setSaving(true);
    setError('');
    try {
      if (editingBoundary) {
        await apiFetch(`${API_BASE}/boundary/${editingBoundary.id}`, {
          method: 'PUT',
          body: JSON.stringify(boundaryForm),
        });
      } else {
        await apiFetch(`${API_BASE}/${selectedAssignmentId}/boundary`, {
          method: 'POST',
          body: JSON.stringify(boundaryForm),
        });
      }
      setBoundaryModalOpen(false);
      await loadScopeData(selectedAssignmentId);
    } catch (e: any) {
      setError(e.message);
    } finally {
      setSaving(false);
    }
  };

  const deleteBoundary = async (id: number) => {
    if (!selectedAssignmentId) return;
    try {
      await apiFetch(`${API_BASE}/boundary/${id}`, { method: 'DELETE' });
      await loadScopeData(selectedAssignmentId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  const approveBoundary = async (id: number) => {
    if (!selectedAssignmentId) return;
    try {
      await apiFetch(`${API_BASE}/boundary/${id}/approve`, { method: 'POST' });
      await loadScopeData(selectedAssignmentId);
    } catch (e: any) {
      setError(e.message);
    }
  };

  /* ---------------------------------------------------------------- */
  /*  Handle delete confirm                                            */
  /* ---------------------------------------------------------------- */

  const handleDeleteConfirm = () => {
    if (!deleteConfirm) return;
    if (deleteConfirm.type === 'scope') {
      deleteScopeItem(deleteConfirm.id);
    } else {
      deleteBoundary(deleteConfirm.id);
    }
    setDeleteConfirm(null);
  };

  /* ---------------------------------------------------------------- */
  /*  Render                                                           */
  /* ---------------------------------------------------------------- */

  if (loadingAssignments) {
    return (
      <div className="flex items-center justify-center h-64 text-slate-500">
        <Loader2 className="w-5 h-5 animate-spin mr-2" />
        Loading...
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6 max-w-7xl mx-auto">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-100 rounded-lg">
            <Target className="w-6 h-6 text-emerald-600" />
          </div>
          <div>
            <div className="flex items-center gap-1"><h1 className="text-3xl font-bold text-slate-900">Scope Definition</h1><PageInfoButton /></div>
            <p className="text-sm text-slate-500">Define in-scope items and boundary statements for framework compliance</p>
          </div>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="flex items-center gap-2 p-3 bg-red-50 border border-red-200 rounded-lg text-red-700 text-sm">
          <AlertTriangle className="w-4 h-4 flex-shrink-0" />
          <span>{error}</span>
          <button onClick={() => setError('')} className="ml-auto text-red-400 hover:text-red-600">&times;</button>
        </div>
      )}

      {/* Framework selector */}
      <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Framework Assignment</label>
        <div className="relative">
          <select
            className="w-full appearance-none bg-slate-50 border border-slate-300 rounded-lg px-4 py-2.5 pr-10 text-sm text-slate-800 focus:outline-none focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 transition"
            value={selectedAssignmentId ?? ''}
            onChange={(e) => setSelectedAssignmentId(e.target.value ? Number(e.target.value) : null)}
          >
            <option value="">Select a framework assignment...</option>
            {assignments.map((a) => (
              <option key={a.id} value={a.id}>
                {a.framework_name || `Framework #${a.id}`}
                {a.version_label ? ` (${a.version_label})` : ''}
              </option>
            ))}
          </select>
          <ChevronDown className="w-4 h-4 text-slate-400 absolute right-3 top-1/2 -translate-y-1/2 pointer-events-none" />
        </div>
      </div>

      {/* Content when assignment selected */}
      {selectedAssignmentId && (
        <>
          {loadingData ? (
            <div className="flex items-center justify-center h-40 text-slate-500">
              <Loader2 className="w-5 h-5 animate-spin mr-2" />
              Loading scope data...
            </div>
          ) : (
            <>
              {/* Summary stats */}
              {summary && (
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  <StatCard label="Total Items" value={summary.total_items} icon={Layers} color="slate" />
                  <StatCard label="Included" value={summary.included} icon={CheckCircle} color="emerald" />
                  <StatCard label="Excluded" value={summary.excluded} icon={XCircle} color="red" />
                  <StatCard label="Approved Statements" value={`${summary.approved_statements}/${summary.total_statements}`} icon={ShieldCheck} color="blue" />
                </div>
              )}

              {/* Scope Items section */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <Layers className="w-5 h-5 text-emerald-600" />
                    <h2 className="text-lg font-semibold text-slate-900">Scope Items</h2>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                      {scopeItems.length}
                    </span>
                  </div>
                  <button
                    onClick={() => openScopeModal()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-emerald-600 text-white text-sm font-medium rounded-lg hover:bg-emerald-700 transition shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Item
                  </button>
                </div>

                {scopeItems.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">
                    <Target className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No scope items defined yet. Add items to define the compliance boundary.</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead>
                        <tr className="bg-slate-50 text-left text-[10px] font-bold text-slate-400 uppercase tracking-wider">
                          <th className="px-5 py-3">Type</th>
                          <th className="px-5 py-3">Name</th>
                          <th className="px-5 py-3 hidden lg:table-cell">Description</th>
                          <th className="px-5 py-3 text-center">Status</th>
                          <th className="px-5 py-3 hidden md:table-cell">Justification</th>
                          <th className="px-5 py-3 text-right">Actions</th>
                        </tr>
                      </thead>
                      <tbody className="divide-y divide-slate-100">
                        {scopeItems.map((item) => {
                          const cfg = SCOPE_TYPE_CONFIG[item.scope_type] ?? SCOPE_TYPE_CONFIG.department;
                          const Icon = cfg.icon;
                          return (
                            <tr key={item.id} className="hover:bg-slate-50/60 transition">
                              <td className="px-5 py-3 text-left">
                                <span className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`}>
                                  <Icon className="w-3.5 h-3.5" />
                                  {cfg.label}
                                </span>
                              </td>
                              <td className="px-5 py-3 text-left font-medium text-slate-900">{item.scope_name}</td>
                              <td className="px-5 py-3 text-left text-slate-500 max-w-xs truncate hidden lg:table-cell">
                                {item.scope_description || '--'}
                              </td>
                              <td className="px-5 py-3 text-left">
                                {item.is_included ? (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-emerald-700 bg-emerald-50 border border-emerald-200 px-2.5 py-1 rounded-full">
                                    <CheckCircle className="w-3.5 h-3.5" />
                                    Included
                                  </span>
                                ) : (
                                  <span className="inline-flex items-center gap-1 text-xs font-medium text-red-700 bg-red-50 border border-red-200 px-2.5 py-1 rounded-full">
                                    <XCircle className="w-3.5 h-3.5" />
                                    Excluded
                                  </span>
                                )}
                              </td>
                              <td className="px-5 py-3 text-left text-slate-500 max-w-xs truncate hidden md:table-cell">
                                {item.justification || '--'}
                              </td>
                              <td className="px-5 py-3 text-left">
                                <div className="flex items-center justify-end gap-1">
                                  <button
                                    onClick={() => openScopeModal(item)}
                                    className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                                    title="Edit"
                                  >
                                    <Pencil className="w-4 h-4" />
                                  </button>
                                  <button
                                    onClick={() => setDeleteConfirm({ type: 'scope', id: item.id })}
                                    className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                                    title="Delete"
                                  >
                                    <Trash2 className="w-4 h-4" />
                                  </button>
                                </div>
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>

              {/* Boundary Statements section */}
              <div className="bg-white border border-slate-200 rounded-xl shadow-sm overflow-hidden">
                <div className="flex items-center justify-between px-5 py-4 border-b border-slate-100">
                  <div className="flex items-center gap-2">
                    <FileText className="w-5 h-5 text-blue-600" />
                    <h2 className="text-lg font-semibold text-slate-900">Boundary Statements</h2>
                    <span className="text-xs bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full font-medium">
                      {boundaries.length}
                    </span>
                  </div>
                  <button
                    onClick={() => openBoundaryModal()}
                    className="inline-flex items-center gap-1.5 px-3.5 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 transition shadow-sm"
                  >
                    <Plus className="w-4 h-4" />
                    Add Statement
                  </button>
                </div>

                {boundaries.length === 0 ? (
                  <div className="py-12 text-center text-slate-400">
                    <FileText className="w-10 h-10 mx-auto mb-2 opacity-40" />
                    <p className="text-sm">No boundary statements defined yet.</p>
                  </div>
                ) : (
                  <div className="divide-y divide-slate-100">
                    {boundaries.map((b) => {
                      const cfg = STATEMENT_TYPE_CONFIG[b.statement_type] ?? STATEMENT_TYPE_CONFIG.inclusion;
                      return (
                        <div key={b.id} className="px-5 py-4 flex items-start gap-4 hover:bg-slate-50/60 transition">
                          <span className={`inline-flex items-center shrink-0 px-2.5 py-1 rounded-full text-xs font-medium border ${cfg.bg} ${cfg.color}`}>
                            {cfg.label}
                          </span>
                          <div className="flex-1 min-w-0">
                            <p className="text-sm text-slate-800 leading-relaxed">{b.statement_text}</p>
                            {b.is_approved && (
                              <p className="text-xs text-emerald-600 mt-1.5 flex items-center gap-1">
                                <ShieldCheck className="w-3.5 h-3.5" />
                                Approved{b.approved_by ? ` by ${b.approved_by}` : ''}
                                {b.approved_at ? ` on ${new Date(b.approved_at).toLocaleDateString()}` : ''}
                              </p>
                            )}
                          </div>
                          <div className="flex items-center gap-1 shrink-0">
                            {!b.is_approved && (
                              <button
                                onClick={() => approveBoundary(b.id)}
                                className="p-1.5 text-slate-400 hover:text-emerald-600 hover:bg-emerald-50 rounded-lg transition"
                                title="Approve"
                              >
                                <ShieldCheck className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => openBoundaryModal(b)}
                              className="p-1.5 text-slate-400 hover:text-blue-600 hover:bg-blue-50 rounded-lg transition"
                              title="Edit"
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={() => setDeleteConfirm({ type: 'boundary', id: b.id })}
                              className="p-1.5 text-slate-400 hover:text-red-600 hover:bg-red-50 rounded-lg transition"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </>
      )}

      {/* No assignment selected placeholder */}
      {!selectedAssignmentId && !loadingAssignments && (
        <div className="bg-white border border-slate-200 rounded-xl shadow-sm py-20 text-center">
          <Target className="w-12 h-12 mx-auto text-slate-300 mb-3" />
          <h3 className="text-lg font-medium text-slate-600 mb-1">Select a Framework</h3>
          <p className="text-sm text-slate-400">Choose a framework assignment above to manage its scope definition.</p>
        </div>
      )}

      {/* ============================================================ */}
      {/* Scope Item Modal                                              */}
      {/* ============================================================ */}
      {scopeModalOpen && (
        <ModalOverlay onClose={() => setScopeModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingScopeItem ? 'Edit Scope Item' : 'Add Scope Item'}
              </h3>
              <button onClick={() => setScopeModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Scope Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Scope Type</label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition"
                  value={scopeForm.scope_type}
                  onChange={(e) => setScopeForm({ ...scopeForm, scope_type: e.target.value as ScopeType })}
                >
                  {SCOPE_TYPES.map((t) => (
                    <option key={t} value={t}>{SCOPE_TYPE_CONFIG[t].label}</option>
                  ))}
                </select>
              </div>
              {/* Name */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Name <span className="text-red-500">*</span></label>
                <input
                  type="text"
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition"
                  placeholder="e.g. Engineering Department"
                  value={scopeForm.scope_name}
                  onChange={(e) => setScopeForm({ ...scopeForm, scope_name: e.target.value })}
                />
              </div>
              {/* Description */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Description</label>
                <textarea
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition resize-none"
                  rows={3}
                  placeholder="Describe this scope item..."
                  value={scopeForm.scope_description}
                  onChange={(e) => setScopeForm({ ...scopeForm, scope_description: e.target.value })}
                />
              </div>
              {/* Included toggle */}
              <div className="flex items-center justify-between">
                <label className="text-sm font-medium text-slate-700">Include in Scope</label>
                <button
                  type="button"
                  onClick={() => setScopeForm({ ...scopeForm, is_included: !scopeForm.is_included })}
                  className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${
                    scopeForm.is_included ? 'bg-emerald-500' : 'bg-slate-300'
                  }`}
                >
                  <span
                    className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform shadow ${
                      scopeForm.is_included ? 'translate-x-6' : 'translate-x-1'
                    }`}
                  />
                </button>
              </div>
              {/* Justification */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Justification</label>
                <textarea
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-emerald-500 focus:border-emerald-500 outline-none transition resize-none"
                  rows={2}
                  placeholder="Reason for inclusion or exclusion..."
                  value={scopeForm.justification}
                  onChange={(e) => setScopeForm({ ...scopeForm, justification: e.target.value })}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setScopeModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveScopeItem}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-emerald-600 rounded-lg hover:bg-emerald-700 disabled:opacity-50 transition"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingScopeItem ? 'Update' : 'Add Item'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* ============================================================ */}
      {/* Boundary Statement Modal                                      */}
      {/* ============================================================ */}
      {boundaryModalOpen && (
        <ModalOverlay onClose={() => setBoundaryModalOpen(false)}>
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-lg mx-4">
            <div className="px-6 py-4 border-b border-slate-100 flex items-center justify-between">
              <h3 className="text-lg font-semibold text-slate-900">
                {editingBoundary ? 'Edit Boundary Statement' : 'Add Boundary Statement'}
              </h3>
              <button onClick={() => setBoundaryModalOpen(false)} className="text-slate-400 hover:text-slate-600 text-xl leading-none">&times;</button>
            </div>
            <div className="px-6 py-5 space-y-4">
              {/* Statement Type */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Statement Type</label>
                <select
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
                  value={boundaryForm.statement_type}
                  onChange={(e) => setBoundaryForm({ ...boundaryForm, statement_type: e.target.value as StatementType })}
                >
                  {STATEMENT_TYPES.map((t) => (
                    <option key={t} value={t}>{STATEMENT_TYPE_CONFIG[t].label}</option>
                  ))}
                </select>
              </div>
              {/* Statement Text */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">Statement Text <span className="text-red-500">*</span></label>
                <textarea
                  className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition resize-none"
                  rows={4}
                  placeholder="Define the boundary statement..."
                  value={boundaryForm.statement_text}
                  onChange={(e) => setBoundaryForm({ ...boundaryForm, statement_text: e.target.value })}
                />
              </div>
            </div>
            <div className="px-6 py-4 border-t border-slate-100 flex justify-end gap-2">
              <button
                onClick={() => setBoundaryModalOpen(false)}
                className="px-4 py-2 text-sm font-medium text-slate-700 bg-slate-100 rounded-lg hover:bg-slate-200 transition"
              >
                Cancel
              </button>
              <button
                onClick={saveBoundary}
                disabled={saving}
                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700 disabled:opacity-50 transition"
              >
                {saving && <Loader2 className="w-4 h-4 animate-spin" />}
                {editingBoundary ? 'Update' : 'Add Statement'}
              </button>
            </div>
          </div>
        </ModalOverlay>
      )}

      {/* Delete confirmation */}
      <ConfirmationModal
        isOpen={!!deleteConfirm}
        onClose={() => setDeleteConfirm(null)}
        onConfirm={handleDeleteConfirm}
        title={deleteConfirm?.type === 'scope' ? 'Delete Scope Item' : 'Delete Boundary Statement'}
        message={
          deleteConfirm?.type === 'scope'
            ? 'Are you sure you want to remove this scope item? This action cannot be undone.'
            : 'Are you sure you want to remove this boundary statement? This action cannot be undone.'
        }
        confirmText="Delete"
        type="danger"
      />
    </div>
  );
};

/* ------------------------------------------------------------------ */
/*  Sub-components                                                     */
/* ------------------------------------------------------------------ */

const StatCard: React.FC<{
  label: string;
  value: string | number;
  icon: React.ElementType;
  color: string;
}> = ({ label, value, icon: Icon, color }) => {
  const colorMap: Record<string, string> = {
    slate:   'bg-slate-100 text-slate-600',
    emerald: 'bg-emerald-100 text-emerald-600',
    red:     'bg-red-100 text-red-600',
    blue:    'bg-blue-100 text-blue-600',
  };
  const cls = colorMap[color] ?? colorMap.slate;

  return (
    <div className="bg-white border border-slate-200 rounded-xl p-4 shadow-sm">
      <div className="flex items-center gap-3">
        <div className={`p-2 rounded-lg ${cls}`}>
          <Icon className="w-5 h-5" />
        </div>
        <div>
          <p className="text-3xl font-bold text-slate-900">{value}</p>
          <p className="text-xs text-slate-500 font-medium">{label}</p>
        </div>
      </div>
    </div>
  );
};

const ModalOverlay: React.FC<{ children: React.ReactNode; onClose: () => void }> = ({ children, onClose }) => (
  <div className="fixed inset-0 z-50 flex items-center justify-center">
    <div className="absolute inset-0 bg-black/40 backdrop-blur-sm" onClick={onClose} />
    <div className="relative z-10">{children}</div>
  </div>
);

export default ScopeDefinition;
